import { useCallback, useEffect, useState } from 'react';
import { Button, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import { CookService } from '../../services/CookServices';
import { formatDate } from '../../utils/DateTimeUtils';
import { maskPhoneNumber } from '../../utils/PhoneNumberUtils';

interface InputProps {
    cookId: number;
}

const cookService = new CookService();

const CookSubscriptionTab = ({ cookId }: InputProps) => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const columns: CommonListColumn[] = [
        { label: 'Id', field: 'id' },
        {
            label: 'Cook',
            render: (row) => <Link href={`/#/Cooks/${row.cookId}/show`}>{row?.cookId}</Link>,
        },
        { label: 'Cook Name', render: (row) => row?.cook?.fname ?? '-' },
        { label: 'Zoho CookId', render: (row) => row?.cook?.zohoCookId ?? '-' },
        { label: 'Cook Mobile', render: (row) => maskPhoneNumber(row?.cook?.mobile) ?? '-' },
        { label: 'Subscription', render: (row) => row?.subscriptionId ?? '-' },
        {
            label: 'User bundle Mapping',
            render: (row) => (
                <Link href={`/#/UserBundles/${row.userBundleMappingId}/show`}>
                    {row?.userBundleMappingId}
                </Link>
            ),
        },
        {
            label: 'Address',
            minWidth: 200,
            render: (row) => {
                const parts = [
                    row?.userBundleMapping?.house?.locality || '',
                    row?.userBundleMapping?.house?.sector || '',
                    row?.userBundleMapping?.house?.address || '',
                    row?.userBundleMapping?.house?.city || '',
                ].filter(Boolean);
                return parts.join(', ') || '-';
            },
        },
        { label: 'Start Slot', render: (row) => row?.startSlot ?? '-' },
        {
            label: 'Start Date',
            nowrap: true,
            render: (row) => (row?.startDate ? formatDate(row.startDate) : '-'),
        },
        {
            label: 'End Date',
            nowrap: true,
            render: (row) => (row?.endDate ? formatDate(row.endDate) : '-'),
        },
        { label: 'Leave Taken', render: (row) => row?.leaveTaken ?? '-' },
        { label: 'Leave Allowed', render: (row) => row?.leaveAllowed ?? '-' },
        { label: 'Zoho', render: (row) => row?.zohoId ?? '-' },
        {
            label: 'Show',
            stopRowClick: true,
            render: (row) => (
                <Button
                    onClick={() =>
                        navigate(
                            `/cookSubscription/show?houseId=${row?.userBundleMapping?.houseId}&subscriptionId=${row?.id}`
                        )
                    }
                >
                    Show
                </Button>
            ),
        },
    ];

    const refreshData = useCallback(() => {
        setLoading(true);
        cookService
            .getAllCookSubscriptionForCook(cookId, rowsPerPage, page * rowsPerPage, {
                order: 'DESC',
                field: 'id',
            })
            .then((res: any) => {
                setData(res?.data ?? []);
                setTotal(res?.total ?? 0);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [cookId, rowsPerPage, page]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    return (
        <CommonListTable
            columns={columns}
            data={data}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            total={total}
            onPageChange={setPage}
            onRowsPerPageChange={(rows) => {
                setRowsPerPage(rows);
                setPage(0);
            }}
            emptyText="No Cook Subscription Found"
        />
    );
};

export default CookSubscriptionTab;
